export const formatDate = (date) => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = months?.[date?.getUTCMonth()];
  const day = date?.getUTCDate();
  const year = date?.getUTCFullYear();
  const hours = String(date?.getUTCHours())?.padStart(2, "0");
  const minutes = String(date?.getUTCMinutes())?.padStart(2, "0");
  return `${month} ${day}, ${year} ${hours}:${minutes} UTC`;
};

export const toUtcWithSameClock = (value) => {
  if (!value) return null;
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return null;
  return new Date(
    Date.UTC(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
      dateValue.getHours(),
      dateValue.getMinutes(),
      dateValue.getSeconds(),
      dateValue.getMilliseconds()
    )
  );
};

export const toLocalDisplayDateFromUtc = (value) => {
  if (!value) return null;
  const dateValue = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateValue.getTime())) return null;
  return new Date(
    dateValue.getUTCFullYear(),
    dateValue.getUTCMonth(),
    dateValue.getUTCDate(),
    dateValue.getUTCHours(),
    dateValue.getUTCMinutes(),
    dateValue.getUTCSeconds(),
    dateValue.getUTCMilliseconds()
  );
};

// Extended generateDateRanges supports custom repetition start/end boundaries and optional daily time window
// Params:
//  type: 'fullYear' | 'eachWeek' | 'eachDay' | 'noRepetition'
//  startDate: Date object base start (for eachDay now source year start is derived from this instead of current year)
//  endDate: Date object base end (for eachDay/eachWeek we constrain loop until this end)
//  dailyStartMinutes / dailyEndMinutes: optional minute-of-day boundaries (0..1440) defining the daily window; if absent falls back to time of startDate/endDate.
export const generateDateRanges = (type, startDate, endDate, dailyStartMinutes = null, dailyEndMinutes = null) => {
  let newRows = [];
  // Use provided start/end year boundaries instead of system current year to allow custom range (e.g., +1 year from startDateAtomData)
  const startBoundary = new Date(startDate);
  const endBoundary = new Date(endDate);
  // Normalize times to midnight for iteration
  const startIter = new Date(Date.UTC(startBoundary.getUTCFullYear(), startBoundary.getUTCMonth(), startBoundary.getUTCDate()));
  const endIter = new Date(Date.UTC(endBoundary.getUTCFullYear(), endBoundary.getUTCMonth(), endBoundary.getUTCDate(), 23, 59, 59, 999));

  // Helper to build Date with daily minutes boundaries
  const buildTime = (baseDate, minutes, end = false) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return new Date(Date.UTC(baseDate.getUTCFullYear(), baseDate.getUTCMonth(), baseDate.getUTCDate(), h, m, end ? 59 : 0, end ? 999 : 0));
  };

  // Avoid mutating iteration date with setHours (which can cause DST/timezone shifts). Build separate Date instances.
  const applyStartEndFromPrototype = (protoDate, targetDate, isEnd = false) => {
    const h = isEnd ? endDate.getUTCHours() : startDate.getUTCHours();
    const m = isEnd ? endDate.getUTCMinutes() : startDate.getUTCMinutes();
    return new Date(Date.UTC(protoDate.getUTCFullYear(), protoDate.getUTCMonth(), protoDate.getUTCDate(), h, m, isEnd ? 59 : 0, isEnd ? 999 : 0));
  };

  if (type === "fullYear") {
    const endTime = endIter.getTime();
    for (let d = new Date(startIter); d.getTime() <= endTime; d.setUTCDate(d.getUTCDate() + 1)) {
      newRows.push({
        startingTime: formatDate(dailyStartMinutes != null ? buildTime(d, dailyStartMinutes) : applyStartEndFromPrototype(d, startDate, false)),
        endingTime: formatDate(dailyEndMinutes != null ? buildTime(d, dailyEndMinutes, true) : applyStartEndFromPrototype(d, endDate, true)),
      });
    }
  } else if (type === "eachWeek") {
    const endTimeWeek = endIter.getTime();
    for (let startOfWeek = new Date(startIter); startOfWeek.getTime() <= endTimeWeek; startOfWeek.setUTCDate(startOfWeek.getUTCDate() + 7)) {
      // Maintain previous behavior: one entry per week start using startOfWeek for time boundaries
      newRows.push({
        startingTime: formatDate(
          dailyStartMinutes != null
            ? buildTime(startOfWeek, dailyStartMinutes)
            : applyStartEndFromPrototype(startOfWeek, startDate, false)
        ),
        endingTime: formatDate(
          dailyEndMinutes != null
            ? buildTime(startOfWeek, dailyEndMinutes, true)
            : applyStartEndFromPrototype(startOfWeek, endDate, true)
        ),
      });
    }
  } else if (type === "eachDay") {
    const endTimeDay = endIter.getTime();
    for (let d = new Date(startIter); d.getTime() <= endTimeDay; d.setUTCDate(d.getUTCDate() + 1)) {
      newRows.push({
        startingTime: formatDate(dailyStartMinutes != null ? buildTime(d, dailyStartMinutes) : applyStartEndFromPrototype(d, startDate, false)),
        endingTime: formatDate(dailyEndMinutes != null ? buildTime(d, dailyEndMinutes, true) : applyStartEndFromPrototype(d, endDate, true)),
      });
    }
  } else if (type === "noRepetition") {
    newRows = [];
  }

  return newRows;
};

// Generate weekly occurrences for a single stacked weekly range (startDay/startMinute -> endDay/endMinute)
// Params:
//   baseStartDate: Date baseline (e.g., startDateAtomData). First occurrence is the first weeklyRange.startDay at startMinute AFTER this baseline.
//   weeklyRange: { startDay, startMinute, endDay, endMinute, wrap }
//   years: number of years forward to generate (default 1)
// Returns array of { startingTime, endingTime } with formatted local times using formatDate.
export const generateWeeklySingleRangeOccurrences = (baseStartDate, weeklyRange, years = 1) => {
  if (!baseStartDate || !weeklyRange) return [];
  const { startDay, startMinute, endDay, endMinute, wrap } = weeklyRange;
  const startBaseline = new Date(baseStartDate);
  const endBoundary = new Date(startBaseline);
  endBoundary.setUTCFullYear(endBoundary.getUTCFullYear() + years);

  // Helper convert minute-of-day to hours/minutes
  const minuteToHM = (min) => {
    if (min === 1440) return { h: 24, m: 0 }; // special boundary (will roll to next day)
    return { h: Math.floor(min / 60), m: min % 60 };
  };

  // Find first occurrence start datetime >= baseline
  const baselineDay = startBaseline.getUTCDay();
  const baselineHMMinutes = startBaseline.getUTCHours() * 60 + startBaseline.getUTCMinutes();
  // Date at start of baseline day (midnight)
  const weekRef = new Date(Date.UTC(startBaseline.getUTCFullYear(), startBaseline.getUTCMonth(), startBaseline.getUTCDate()));
  const diffToStartDay = startDay - weekRef.getUTCDay();
  const candidateDate = new Date(weekRef);
  candidateDate.setUTCDate(candidateDate.getUTCDate() + diffToStartDay);
  const startHM = minuteToHM(startMinute);
  let firstStart = new Date(Date.UTC(candidateDate.getUTCFullYear(), candidateDate.getUTCMonth(), candidateDate.getUTCDate(), startHM.h === 24 ? 0 : startHM.h, startHM.m));
  // If 24:00 (minute 1440) advance a day
  if (startHM.h === 24) {
    firstStart.setUTCDate(firstStart.getUTCDate() + 1);
  }
  // If candidate earlier than baseline OR same week but time before baseline's time when baseline day == startDay
  if (firstStart <= startBaseline || (baselineDay === startDay && startMinute <= baselineHMMinutes)) {
    firstStart.setDate(firstStart.getDate() + 7); // advance to next week occurrence
  }

  const ranges = [];
  const endTimeBoundary = endBoundary.getTime();
  for (; firstStart.getTime() < endTimeBoundary; firstStart.setUTCDate(firstStart.getUTCDate() + 7)) {
    // Compute end occurrence
    let daysSpan;
    if (!wrap) {
      daysSpan = endDay - startDay;
      if (daysSpan < 0) daysSpan = 0; // safety
    } else {
      daysSpan = (7 - startDay) + endDay; // wrap across week boundary
    }
    const endHM = minuteToHM(endMinute);
    let endOccurrence = new Date(firstStart);
    endOccurrence.setUTCDate(endOccurrence.getUTCDate() + daysSpan);
    endOccurrence.setUTCHours(endHM.h === 24 ? 0 : endHM.h, endHM.m, 0, 0);
    if (endHM.h === 24) {
      endOccurrence.setUTCDate(endOccurrence.getUTCDate() + 1); // roll over midnight boundary
    }
    // Push formatted row
    ranges.push({
      startingTime: formatDate(firstStart),
      endingTime: formatDate(endOccurrence),
    });
  }
  return ranges;
};
